import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { DOMESTIC, FLEET, INTERNATIONAL } from "../lib/site";
import { MaskLines, Reveal, SectionEyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPTIONS = [
  ...DOMESTIC.map((d) => `${d.name} (Domestic)`),
  ...INTERNATIONAL.map((d) => `${d.name} (International)`),
  ...FLEET.map((v) => `${v.type} – ${v.name} (Rental)`),
  "Flight Booking",
  "Custom / Other",
];
export const matchOption = (name) => OPTIONS.find((o) => o.startsWith(name)) || "";

const field = "w-full rounded-2xl border-2 border-zinc-200 bg-white px-4 py-3.5 text-ink placeholder:text-zinc-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-go focus:shadow-[0_0_0_4px_rgba(255,79,0,0.15)]";
const label = "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-700";

export const Inquiry = ({ selection, setSelection }) => {
  const [form, setForm] = useState({ full_name: "", phone: "", message: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selection) { toast.error("Please choose a destination or vehicle"); return; }
    setState("loading");
    try {
      await axios.post(`${API}/inquiry`, { ...form, selection });
      setState("done");
      toast.success("Inquiry sent! We'll get back to you shortly.");
      setForm({ full_name: "", phone: "", message: "" });
      setSelection("");
    } catch (err) {
      setState("idle");
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send. Please check your details or WhatsApp us.");
    }
  };

  return (
    <section id="contact" className="relative bg-paper py-24 sm:py-32" data-testid="inquiry-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionEyebrow index="04" label="Quick Email Inquiry" />
          <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold leading-[0.92] tracking-[-0.035em] text-ink">
            <MaskLines lines={["Send Us an", <span key="i">Inquiry<span className="text-go">.</span></span>]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-sm text-base md:text-lg text-zinc-700">Have a custom request or question? Drop us a message!</p>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="rounded-[32px] bg-white p-6 sm:p-10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.35)] ring-1 ring-zinc-100" data-testid="inquiry-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="fn">Full Name</label>
                <input id="fn" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder="Your name" className={field} data-testid="inquiry-name-input" />
              </div>
              <div>
                <label className={label} htmlFor="ph">Phone Number</label>
                <input id="ph" required type="tel" pattern="[0-9+\-\s()]{7,20}" value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxx" className={field} data-testid="inquiry-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={label} htmlFor="sel">Destination / Vehicle Selected</label>
                <select id="sel" required value={selection} onChange={(e) => setSelection(e.target.value)} className={`${field} appearance-none`} data-testid="inquiry-selection-select">
                  <option value="" disabled>Choose a destination or vehicle</option>
                  {OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={label} htmlFor="msg">Message</label>
                <textarea id="msg" rows={4} maxLength={1500} value={form.message} onChange={set("message")} placeholder="Travel dates, number of people, special requests…" className={`${field} resize-none`} data-testid="inquiry-message-input" />
              </div>
            </div>
            <button type="submit" disabled={state === "loading"} data-testid="inquiry-submit-btn"
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-go py-4 font-bold text-ink transition-[background-color,color,transform] duration-300 hover:bg-ink hover:text-white active:scale-[0.98] disabled:opacity-70">
              {state === "loading" ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />}
              {state === "loading" ? "Sending…" : "Send Inquiry"}
            </button>
            {state === "done" && (
              <p className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-emerald-700" data-testid="inquiry-success-msg">
                <CheckCircle2 size={16} /> Thank you! Your inquiry has been sent to our team.
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
